/* eslint-disable no-use-before-define */

/* --------------------------------------------------------------------------
 *  START: ORIGINAL (NON-MODIFIED) base
 *  Only modified methods have been moved down
 * vvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvv */

const focusHelper = require('base/components/focus');
const { default: getFrontConfig } = require('../common/getFrontConfig');

/**
 * Retrieves the value associated with the Quantity pull-down menu
 * @param {jquery} $el - DOM container for the relevant quantity
 * @return {string} - value found in the quantity input
 */
function getQuantitySelected($el) {
  const $closest = $el.closest('.product-detail');
  const isProductCard = $closest.is('.product-card');
  const isSFLLimited = $el.data('is-limited');

  if (isSFLLimited) {
    return $el.data('qty-to-add');
  }

  if (isProductCard) {
    return $('.quantity-label', $closest).data('value');
  }

  return getQuantitySelector($el).val() || 1;
}

/**
 * Process the attribute values for an attribute that has image swatches
 *
 * @param {Object} attr - Attribute
 * @param {string} attr.id - Attribute ID
 * @param {Object[]} attr.values - Array of attribute value objects
 * @param {string} attr.values.value - Attribute coded value
 * @param {string} attr.values.url - URL to de/select an attribute value of the product
 * @param {boolean} attr.values.isSelectable - Flag as to whether an attribute value can be
 *     selected.  If there is no variant that corresponds to a specific combination of attribute
 *     values, an attribute may be disabled in the Product Detail Page
 * @param {jQuery} $productContainer - DOM container for a given product
 * @param {Object} msgs - object containing resource messages
 */
function processSwatchValues(attr, $productContainer, msgs) {
  attr.values.forEach((attrValue) => {
    const $attrValue = $productContainer.find(
      `[data-attr="${attr.id}"] [data-attr-value="${attrValue.value}"]`
    );
    const $swatchButton = $attrValue.parent();

    if (attrValue.selected) {
      $attrValue.addClass('selected');
      $attrValue
        .siblings('.selected-assistive-text')
        .text(msgs.assistiveSelectedText);
    } else {
      $attrValue.removeClass('selected');
      $attrValue.siblings('.selected-assistive-text').empty();
    }

    if (attrValue.url) {
      $swatchButton.attr('data-url', attrValue.url);
    } else {
      $swatchButton.removeAttr('data-url');
    }

    // Disable if not selectable
    $attrValue.removeClass('selectable unselectable');

    $attrValue.addClass(attrValue.selectable ? 'selectable' : 'unselectable');
  });
}

/**
 * Process attribute values associated with an attribute that does not have image swatches
 *
 * @param {Object} attr - Attribute
 * @param {string} attr.id - Attribute ID
 * @param {Object[]} attr.values - Array of attribute value objects
 * @param {string} attr.values.value - Attribute coded value
 * @param {string} attr.values.url - URL to de/select an attribute value of the product
 * @param {boolean} attr.values.isSelectable - Flag as to whether an attribute value can be
 *     selected.  If there is no variant that corresponds to a specific combination of attribute
 *     values, an attribute may be disabled in the Product Detail Page
 * @param {jQuery} $productContainer - DOM container for a given product
 */
function processNonSwatchValues(attr, $productContainer) {
  const $attr = `[data-attr="${attr.id}"]`;
  const $defaultOption = $productContainer.find(
    `${$attr} .select-${attr.id} option:first`
  );
  $defaultOption.attr('value', attr.resetUrl);

  attr.values.forEach((attrValue) => {
    const $attrValue = $productContainer.find(
      `${$attr} [data-attr-value="${attrValue.value}"]`
    );
    $attrValue.attr('value', attrValue.url).removeAttr('disabled');

    if (!attrValue.selectable) {
      $attrValue.attr('disabled', true);
    }
  });
}

/**
 * Routes the handling of attribute processing depending on whether the attribute has image
 *     swatches or not
 *
 * @param {Object} attrs - Attribute
 * @param {string} attr.id - Attribute ID
 * @param {jQuery} $productContainer - DOM element for a given product
 * @param {Object} msgs - object containing resource messages
 */
function updateAttrs(attrs, $productContainer, msgs) {
  // Currently, the only attribute type that has image swatches is Color.
  const attrsWithSwatches = ['color'];

  attrs.forEach((attr) => {
    if (attrsWithSwatches.indexOf(attr.id) > -1) {
      processSwatchValues(attr, $productContainer, msgs);
    } else {
      processNonSwatchValues(attr, $productContainer);
    }
  });
}

/**
 * Updates the availability status in the Product Detail Page
 *
 * @param {Object} response - Ajax response object after an
 *                            attribute value has been [de]selected
 * @param {jQuery} $productContainer - DOM element for a given product
 */
function updateAvailability(response, $productContainer) {
  let availabilityValue = '';
  const availabilityMessages = response.product.availability.messages;
  if (!response.product.readyToOrder) {
    availabilityValue = `<li><div>${response.resources.info_selectforstock}</div></li>`;
  } else {
    availabilityMessages.forEach((message) => {
      availabilityValue += `<li><div>${message}</div></li>`;
    });
  }

  $($productContainer).trigger('product:updateAvailability', {
    product: response.product,
    $productContainer,
    message: availabilityValue,
    resources: response.resources,
  });
}

/**
 * Generates html for product attributes section
 *
 * @param {array} attributes - list of attributes
 * @return {string} - Compiled HTML
 */
function getAttributesHtml(attributes) {
  if (!attributes) {
    return '';
  }

  let html = '';

  attributes.forEach((attributeGroup) => {
    if (attributeGroup.ID === 'mainAttributes') {
      attributeGroup.attributes.forEach((attribute) => {
        html += `<div class="attribute-values">${attribute.label}: ${attribute.value}</div>`;
      });
    }
  });

  return html;
}

/**
 * @typedef UpdatedOptionValue
 * @type Object
 * @property {string} id - Option value ID for look up
 * @property {string} url - Updated option value selection URL
 */

/**
 * @typedef OptionSelectionResponse
 * @type Object
 * @property {string} priceHtml - Updated price HTML code
 * @property {Object} options - Updated Options
 * @property {string} options.id - Option ID
 * @property {UpdatedOptionValue[]} options.values - Option values
 */

/**
 * Updates DOM using post-option selection Ajax response
 *
 * @param {OptionSelectionResponse} optionsHtml - Ajax response optionsHtml from selecting a product option
 * @param {jQuery} $productContainer - DOM element for current product
 */
function updateOptions(optionsHtml, $productContainer) {
  // Update options
  $productContainer.find('.product-options').empty().html(optionsHtml);
}

/**
 * Dynamically creates Bootstrap carousel from response containing images
 * @param {Object[]} imgs - Array of large product images,along with related information
 * @param {jQuery} $productContainer - DOM element for a given product
 */
function createCarousel(imgs, $productContainer) {
  const carousel = $productContainer.find('.carousel');
  $(carousel).carousel('dispose');
  const carouselId = $(carousel).attr('id');
  $(carousel)
    .empty()
    .append(
      `<ol class="carousel-indicators"></ol><div class="carousel-inner" role="listbox"></div><a class="carousel-control-prev" href="#${carouselId}" role="button" data-slide="prev"><span class="fa icon-prev" aria-hidden="true"></span><span class="sr-only">${$(
        carousel
      ).data(
        'prev'
      )}</span></a><a class="carousel-control-next" href="#${carouselId}" role="button" data-slide="next"><span class="fa icon-next" aria-hidden="true"></span><span class="sr-only">${$(
        carousel
      ).data('next')}</span></a>`
    );
  for (let i = 0; i < imgs.length; i++) {
    $(
      `<div class="carousel-item"><img src="${
        imgs[i].url
      }" class="d-block img-fluid" alt="${imgs[i].alt} image number ${parseInt(
        imgs[i].index,
        10
      )}" itemprop="image" /></div>`
    ).appendTo($(carousel).find('.carousel-inner'));
    $(
      `<li data-target="#${carouselId}" data-slide-to="${i}" class=""></li>`
    ).appendTo($(carousel).find('.carousel-indicators'));
  }
  $($(carousel).find('.carousel-item')).first().addClass('active');
  $($(carousel).find('.carousel-indicators > li')).first().addClass('active');
  if (imgs.length === 1) {
    $(
      $(carousel).find('.carousel-indicators, a[class^="carousel-control-"]')
    ).detach();
  }
  $(carousel).carousel();
  $($(carousel).find('.carousel-indicators')).attr('aria-hidden', true);
}

/**
 * Parses JSON from Ajax call made whenever an attribute value is [de]selected
 * @param {Object} response - response from Ajax call
 * @param {Object} response.product - Product object
 * @param {string} response.product.id - Product ID
 * @param {Object[]} response.product.variationAttributes - Product attributes
 * @param {Object[]} response.product.images - Product images
 * @param {boolean} response.product.hasRequiredAttrsSelected - Flag as to whether all required
 *     attributes have been selected.  Used partially to
 *     determine whether the Add to Cart button can be enabled
 * @param {jQuery} $productContainer - DOM element for a given product.
 */
function handleVariantResponse(response, $productContainer) {
  const isChoiceOfBonusProducts =
    $productContainer.parents('.choose-bonus-product-dialog').length > 0;
  let isVaraint;
  if (response.product.variationAttributes) {
    updateAttrs(
      response.product.variationAttributes,
      $productContainer,
      response.resources
    );
    isVaraint = response.product.productType === 'variant';
    if (isChoiceOfBonusProducts && isVaraint) {
      $productContainer
        .parent('.bonus-product-item')
        .data('pid', response.product.id);

      $productContainer
        .parent('.bonus-product-item')
        .data('ready-to-order', response.product.readyToOrder);
    }
  }

  // Update primary images
  const FRONT_CONFIG = getFrontConfig();
  const primaryImageUrls =
    response.product.images[FRONT_CONFIG.productImages.imageTypes.PDP_480];
  const isQuickView = $productContainer.hasClass('product-quickview');
  if (primaryImageUrls && !isQuickView) {
    createCarousel(primaryImageUrls, $productContainer);
  }
  // Update pricing
  if (!isChoiceOfBonusProducts) {
    const $priceSelector = $('.prices .price', $productContainer).length
      ? $('.prices .price', $productContainer)
      : $('.prices .price');
    $priceSelector.replaceWith(response.product.price.html);
  }

  // Update promotions
  $productContainer
    .find('.promotions')
    .empty()
    .html(response.product.promotionsHtml);

  updateAvailability(response, $productContainer);

  if (isChoiceOfBonusProducts) {
    const $selectButton = $productContainer.find('.select-bonus-product');
    $selectButton.trigger('bonusproduct:updateSelectButton', {
      product: response.product,
      $productContainer,
    });
  } else {
    // Enable "Add to Cart" button if all required attributes have been selected
    $(
      'button.add-to-cart, button.add-to-cart-global, button.update-cart-product-global'
    )
      .trigger('product:updateAddToCart', {
        product: response.product,
        $productContainer,
      })
      .trigger('product:statusUpdate', response.product);
  }

  // Update attributes
  $productContainer
    .find('.main-attributes')
    .empty()
    .html(getAttributesHtml(response.product.attributes));
}

/**
 * @typespec UpdatedQuantity
 * @type Object
 * @property {boolean} selected - Whether the quantity has been selected
 * @property {string} value - The number of products to purchase
 * @property {string} url - Compiled URL that specifies variation attributes, product ID, options,
 *     etc.
 */

/**
 * Updates the quantity DOM elements post Ajax call
 * @param {UpdatedQuantity[]} quantities -
 * @param {jQuery} $productContainer - DOM container for a given product
 */
function updateQuantities(quantities, $productContainer) {
  if ($productContainer.parent('.bonus-product-item').length <= 0) {
    const optionsHtml = quantities
      .map((quantity) => {
        const selected = quantity.selected ? ' selected ' : '';
        return `<option value="${quantity.value}"  data-url="${quantity.url}"${selected}>${quantity.value}</option>`;
      })
      .join('');
    getQuantitySelector($productContainer).empty().html(optionsHtml);
  }
}

/**
 * Retrieves url to use when adding a product to the cart
 *
 * @return {string} - The provided URL to use when adding a product to the cart
 */
function getAddToCartUrl() {
  return $('.add-to-cart-url').val();
}

/**
 * Parses the html for a modal window
 * @param {string} html - representing the body and footer of the modal window
 *
 * @return {Object} - Object with properties body and footer.
 */
function parseHtml(html) {
  const $html = $('<div>').append($.parseHTML(html));

  const body = $html.find('.choice-of-bonus-product');
  const footer = $html.find('.modal-footer').children();

  return { body, footer };
}

/**
 * Retrieves url to use when adding a product to the cart
 *
 * @param {Object} data - data object used to fill in dynamic portions of the html
 */
function chooseBonusProducts(data) {
  $('.modal-body').spinner().start();

  if ($('#chooseBonusProductModal').length !== 0) {
    $('#chooseBonusProductModal').remove();
  }
  let bonusUrl;
  if (data.bonusChoiceRuleBased) {
    bonusUrl = data.showProductsUrlRuleBased;
  } else {
    bonusUrl = data.showProductsUrlListBased;
  }

  const htmlString =
    `${
      '<!-- Modal -->' +
      '<div class="modal fade" id="chooseBonusProductModal" tabindex="-1" role="dialog">' +
      '<span class="enter-message sr-only" ></span>' +
      '<div class="modal-dialog choose-bonus-product-dialog" ' +
      'data-total-qty="'
    }${data.maxBonusItems}"` +
    `data-UUID="${data.uuid}"` +
    `data-pliUUID="${data.pliUUID}"` +
    `data-addToCartUrl="${data.addToCartUrl}"` +
    'data-pageStart="0"' +
    `data-pageSize="${data.pageSize}"` +
    `data-moreURL="${data.showProductsUrlRuleBased}"` +
    `data-bonusChoiceRuleBased="${data.bonusChoiceRuleBased}">` +
    '<!-- Modal content-->' +
    '<div class="modal-content">' +
    '<div class="modal-header">' +
    `    <span class="">${data.labels.selectprods}</span>` +
    '    <button type="button" class="close pull-right" data-dismiss="modal">' +
    '        <span aria-hidden="true">&times;</span>' +
    '        <span class="sr-only"> </span>' +
    '    </button>' +
    '</div>' +
    '<div class="modal-body"></div>' +
    '<div class="modal-footer"></div>' +
    '</div>' +
    '</div>' +
    '</div>';
  $('body').append(htmlString);
  $('.modal-body').spinner().start();

  $.ajax({
    url: bonusUrl,
    method: 'GET',
    dataType: 'json',
    success(response) {
      const parsedHtml = parseHtml(response.renderedTemplate);
      $('#chooseBonusProductModal .modal-body').empty();
      $('#chooseBonusProductModal .enter-message').text(
        response.enterDialogMessage
      );
      $('#chooseBonusProductModal .modal-header .close .sr-only').text(
        response.closeButtonText
      );
      $('#chooseBonusProductModal .modal-body').html(parsedHtml.body);
      $('#chooseBonusProductModal .modal-footer').html(parsedHtml.footer);
      $('#chooseBonusProductModal').modal('show');
      $.spinner().stop();
    },
    error() {
      $.spinner().stop();
    },
  });
}

/**
 * Updates the Mini-Cart quantity value after the customer has pressed the "Add to Cart" button
 * @param {string} response - ajax response from clicking the add to cart button
 */
function handlePostCartAdd(response) {
  $('.minicart').trigger('count:update', response);
  // show add to cart toast
  if (
    response.newBonusDiscountLineItem &&
    Object.keys(response.newBonusDiscountLineItem).length !== 0
  ) {
    chooseBonusProducts(response.newBonusDiscountLineItem);
  }
}

/**
 * Retrieves the bundle product item ID's for the Controller to replace bundle master product
 * items with their selected variants
 *
 * @return {string[]} - List of selected bundle product item ID's
 */
function getChildProducts() {
  const childProducts = [];
  $('.bundle-item').each(function getEachBundleItem() {
    childProducts.push({
      pid: $(this).find('.product-id').text(),
      quantity: parseInt($(this).find('label.quantity').data('quantity'), 10),
    });
  });

  return childProducts.length ? JSON.stringify(childProducts) : [];
}

/**
 * Retrieve product options
 *
 * @param {jQuery} $productContainer - DOM element for current product
 * @return {string} - Product options and their selected values
 */
function getOptions($productContainer) {
  const options = $productContainer
    .find('.product-option')
    .map(function buildEachOption() {
      const $elOption = $(this).find('.options-select');
      const urlValue = $elOption.val();
      const selectedValueId = $elOption
        .find(`option[value="${urlValue}"]`)
        .data('value-id');
      return {
        optionId: $(this).data('option-id'),
        selectedValueId,
      };
    })
    .toArray();

  return JSON.stringify(options);
}

/**
 * Makes a call to the server to report the event of adding an item to the cart
 *
 * @param {string | boolean} url - a string representing the end point to hit so that the event can be recorded, or false
 */
function miniCartReportingUrl(url) {
  if (url) {
    $.ajax({
      url,
      method: 'GET',
      success() {
        // reporting urls hit on the server
      },
      error() {
        // no reporting urls hit on the server
      },
    });
  }
}

function updateProductSelectors($sizeSelector, $quantitySelector) {
  const QUANTITY_REGEX = /quantity=\d*/gm;
  const baseUrl = $sizeSelector?.val();
  const selectedQuantity = $quantitySelector?.find('option:selected');
  if (baseUrl && selectedQuantity) {
    const finalUrl = baseUrl.replace(
      QUANTITY_REGEX,
      `quantity=${selectedQuantity.text()}`
    );
    selectedQuantity.attr('data-url', finalUrl);
  }
}

const baseModuleExports = {
  attributeSelect,
  methods: {
    editBonusProducts(data) {
      chooseBonusProducts(data);
    },
    addToCartHandler,
  },

  focusChooseBonusProductModal() {
    $('body').on('shown.bs.modal', '#chooseBonusProductModal', () => {
      $('#chooseBonusProductModal').siblings().attr('aria-hidden', 'true');
      $('#chooseBonusProductModal .close').focus();
    });
  },

  onClosingChooseBonusProductModal() {
    $('body').on('hidden.bs.modal', '#chooseBonusProductModal', () => {
      $('#chooseBonusProductModal').siblings().attr('aria-hidden', 'false');
    });
  },

  trapChooseBonusProductModalFocus() {
    $('body').on('keydown', '#chooseBonusProductModal', (e) => {
      const focusParams = {
        event: e,
        containerSelector: '#chooseBonusProductModal',
        firstElementSelector: '.close',
        lastElementSelector: '.add-bonus-products',
      };
      focusHelper.setTabNextFocus(focusParams);
    });
  },

  colorAttribute() {
    $(document).on(
      'click',
      '[data-attr="color"] button',
      function selectColorHandler(e) {
        e.preventDefault();

        if ($(this).attr('disabled')) {
          return;
        }
        let $productContainer = $(this).closest('.set-item');
        if (!$productContainer.length) {
          $productContainer = $(this).closest('.product-detail');
        }

        attributeSelect($(this).attr('data-url'), $productContainer);
      }
    );
  },

  selectAttribute() {
    $(document).on(
      'change',
      '.product-detail select[class*="select-"]:not(.select--primary), .options-select',
      function selectChangeHandler(e) {
        e.preventDefault();

        let $productContainer = $(this).closest('.set-item');
        if (!$productContainer.length) {
          $productContainer = $(this).closest('.product-detail');
        }
        updateProductSelectors(
          $(e.currentTarget),
          $productContainer.find('.quantity-select')
        );
        attributeSelect(e.currentTarget.value, $productContainer);
      }
    );
  },

  availability() {
    $(document).on(
      'change',
      '.quantity-select',
      function quantityChangeHandler(e) {
        e.preventDefault();

        let $productContainer = $(this).closest('.product-detail');
        if (!$productContainer.length) {
          $productContainer = $(this)
            .closest('.modal-content')
            .find('.product-quickview');
        }

        if ($('.bundle-items', $productContainer).length === 0) {
          updateProductSelectors(
            $productContainer.find('.select-size'),
            $(e.currentTarget)
          );
          attributeSelect(
            $(e.currentTarget).find('option:selected').data('url'),
            $productContainer
          );
        }
      }
    );
  },

  // Este método está sobreescrito más abajo
  addToCart() {},

  selectBonusProduct() {
    $(document).on(
      'click',
      '.select-bonus-product',
      function selectBonusProductHandler() {
        const $choiceOfBonusProduct = $(this).parents(
          '.choice-of-bonus-product'
        );
        const pid = $(this).data('pid');
        const maxPids = $('.choose-bonus-product-dialog').data('total-qty');
        const submittedQty = parseInt(
          $choiceOfBonusProduct.find('.bonus-quantity-select').val(),
          10
        );
        let totalQty = 0;
        $.each(
          $('#chooseBonusProductModal .selected-bonus-products .selected-pid'),
          function countTotalQty() {
            totalQty += $(this).data('qty');
          }
        );
        totalQty += submittedQty;
        const optionID = $choiceOfBonusProduct
          .find('.product-option')
          .data('option-id');
        const valueId = $choiceOfBonusProduct
          .find('.options-select option:selected')
          .data('valueId');
        if (totalQty <= maxPids) {
          const selectedBonusProductHtml =
            // eslint-disable-next-line no-useless-concat
            `${'' + '<div class="selected-pid row" ' + 'data-pid="'}${pid}"` +
            `data-qty="${submittedQty}"` +
            `data-optionID="${optionID || ''}"` +
            `data-option-selected-value="${valueId || ''}"` +
            '>' +
            `<div class="col-sm-11 col-9 bonus-product-name" >${$choiceOfBonusProduct
              .find('.product-name')
              .html()}</div>` +
            '<div class="col-1"><i class="fa fa-times" aria-hidden="true"></i></div>' +
            '</div>';
          $('#chooseBonusProductModal .selected-bonus-products').append(
            selectedBonusProductHtml
          );
          $('.pre-cart-products').html(totalQty);
          $('.selected-bonus-products .bonus-summary').removeClass(
            'alert-danger'
          );
        } else {
          $('.selected-bonus-products .bonus-summary').addClass('alert-danger');
        }
      }
    );
  },
  removeBonusProduct() {
    $(document).on(
      'click',
      '.selected-pid',
      function removeBonusProductHandler() {
        $(this).remove();
        const $selected = $(
          '#chooseBonusProductModal .selected-bonus-products .selected-pid'
        );
        let count = 0;
        if ($selected.length) {
          $selected.each(function countQty() {
            count += parseInt($(this).data('qty'), 10);
          });
        }

        $('.pre-cart-products').html(count);
        $('.selected-bonus-products .bonus-summary').removeClass(
          'alert-danger'
        );
      }
    );
  },
  enableBonusProductSelection() {
    $('body').on('bonusproduct:updateSelectButton', (e, response) => {
      $('button.select-bonus-product', response.$productContainer).attr(
        'disabled',
        !response.product.readyToOrder || !response.product.available
      );
      const pid = response.product.id;
      $('button.select-bonus-product', response.$productContainer).data(
        'pid',
        pid
      );
    });
  },
  showMoreBonusProducts() {
    $(document).on(
      'click',
      '.show-more-bonus-products',
      function showMoreBonusProductsHandler() {
        const url = $(this).data('url');
        $('.modal-content').spinner().start();
        $.ajax({
          url,
          method: 'GET',
          success(html) {
            const parsedHtml = parseHtml(html);
            $('.modal-body').append(parsedHtml.body);
            $('.show-more-bonus-products:first').remove();
            $('.modal-content').spinner().stop();
          },
          error() {
            $('.modal-content').spinner().stop();
          },
        });
      }
    );
  },
  addBonusProductsToCart() {
    $(document).on('click', '.add-bonus-products', () => {
      const $readyToOrderBonusProducts = $(
        '.choose-bonus-product-dialog .selected-pid'
      );
      let queryString = '?pids=';
      const url = $('.choose-bonus-product-dialog').data('addtocarturl');
      const pidsObject = {
        bonusProducts: [],
      };

      $.each($readyToOrderBonusProducts, function buildBonusProducts() {
        const qtyOption = parseInt($(this).data('qty'), 10);

        let option = null;
        if (qtyOption > 0) {
          if (
            $(this).data('optionid') &&
            $(this).data('option-selected-value')
          ) {
            option = {};
            option.optionId = $(this).data('optionid');
            option.productId = $(this).data('pid');
            option.selectedValueId = $(this).data('option-selected-value');
          }
          pidsObject.bonusProducts.push({
            pid: $(this).data('pid'),
            qty: qtyOption,
            options: [option],
          });
          pidsObject.totalQty = parseInt($('.pre-cart-products').html(), 10);
        }
      });
      queryString += JSON.stringify(pidsObject);
      queryString = `${queryString}&uuid=${$(
        '.choose-bonus-product-dialog'
      ).data('uuid')}`;
      queryString = `${queryString}&pliuuid=${$(
        '.choose-bonus-product-dialog'
      ).data('pliuuid')}`;
      $.spinner().start();
      $.ajax({
        url: url + queryString,
        method: 'POST',
        success(data) {
          $.spinner().stop();
          if (data.error) {
            $('#chooseBonusProductModal').modal('hide');
          } else {
            $('.configure-bonus-product-attributes').html(data);
            $('.bonus-products-step2').removeClass('hidden-xl-down');
            $('#chooseBonusProductModal').modal('hide');
            $('.minicart-quantity').html(data.totalQty);
            setTimeout(() => {
              $('.add-to-basket-alert').remove();
              if ($('.cart-content').length) {
                window.location.reload();
              }
            }, 1500);
          }
        },
        error() {
          $.spinner().stop();
        },
      });
    });
  },

  // getPidValue: getPidValue,
  getQuantitySelected,
  miniCartReportingUrl,
};

/* ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
 *  END: ORIGINAL (NON-MODIFIED) base
 * -------------------------------------------------------------------------- */

/* --------------------------------------------------------------------------
 *  START: OVERRIDEN private methods
 * vvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvvv */

/**
 * updates the product view when a product attribute is selected or deselected
 * or when changing quantity
 * @param {string} selectedValueUrl - the Url for the selected variation value
 * @param {jQuery} $productContainer - DOM element for current product
 */
async function attributeSelect(selectedValueUrl, $productContainer) {
  let asyncResponse;
  if (selectedValueUrl) {
    $('body').trigger('product:beforeAttributeSelect', {
      url: selectedValueUrl,
      container: $productContainer,
    });

    await $.ajax({
      url: selectedValueUrl,
      method: 'GET',
      success(data) {
        if (!data.error) {
          if (!isVueProductContainer($productContainer)) {
            // SFRA response processing
            handleVariantResponse(data, $productContainer);
            updateOptions(data.product.optionsHtml, $productContainer);
            updateQuantities(data.product.quantities, $productContainer);
          }
          $('body').trigger('product:afterAttributeSelect', {
            data,
            container: $productContainer,
          });
          $.spinner().stop();
          asyncResponse = data;
        } else {
          /* eslint-disable-next-line no-console */
          console.warn(data.errorMessage);
        }
      },
      error() {
        $.spinner().stop();
      },
    });
  }
  return asyncResponse;
}

/**
 * Retrieve contextual quantity selector
 * @param {jquery} $el - DOM container for the relevant quantity
 * @return {jquery} - quantity selector DOM container
 */
function getQuantitySelector($el) {
  const $closest = $el.closest('.product-detail');
  const isVueComponent = $closest.is('.product-detail-vue');
  if (isVueComponent) {
    return $closest.find('.quantity-select');
  }
  // SFRA original getQuantitySelector
  let quantitySelected;
  if ($el && $('.set-items').length) {
    quantitySelected = $($el)
      .closest('.product-detail')
      .find('.quantity-select');
  } else if ($el && $('.product-bundle').length) {
    const quantitySelectedModal = $($el)
      .closest('.modal-footer')
      .find('.quantity-select');
    const quantitySelectedPDP = $($el)
      .closest('.bundle-footer')
      .find('.quantity-select');
    if (quantitySelectedModal.val() === undefined) {
      quantitySelected = quantitySelectedPDP;
    } else {
      quantitySelected = quantitySelectedModal;
    }
  } else {
    quantitySelected = $('.quantity-select');
  }
  return quantitySelected;
}

/**
 * Retrieves the relevant pid value
 * @param {jquery} $el - DOM container for a given add to cart button
 * @return {string} - value to be used when adding product to cart
 */
function getPidValue($el) {
  let pid;

  const $closest = $el.closest('.product-detail');
  const isVueComponent = $closest.is('.product-detail-vue');
  const isProductCard = $closest.is('.product-card');
  const isCustomizationModal = $el.closest(
    '.the-product-details-customization-modal'
  );

  // TODO It should take the pid from the element (button) itself whenever it's possible
  if (isCustomizationModal.length > 0 && $el) {
    pid = $el[0].dataset.pid;
    const productWrapper = $(
      `.product-wrapper.product-detail-vue[data-pid="${pid}"]`
    );
    if (productWrapper) {
      pid = productWrapper.find('.product-id').text();
    }
  } else if (isVueComponent) {
    pid = $closest.find('.product-id').text();
  } else if (isProductCard) {
    pid = $closest.data('product-id');
  }
  // SFRA original getPidValue
  else if ($('#quickViewModal').hasClass('show') && !$('.product-set').length) {
    pid = $($el)
      .closest('.modal-content')
      .find('.product-quickview')
      .data('pid');
  } else if ($('.product-set-detail').length || $('.product-set').length) {
    pid = $($el).closest('.product-detail').find('.product-id').text();
  } else {
    pid = $('.product-detail:not(".bundle-item")').data('pid');
  }

  return pid;
}

/* ^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
 *  END: OVERRIDEN private methods
 * -------------------------------------------------------------------------- */

/**
 * Sobreescribe el addToCart
 * @param {Element} element - DOM container for a given add to cart button
 * @param {Object} [options] - Extra details the product and the tile.
 * @param {BaseProductTileContext} [options.tileContext] - UI context origin.
 * @param {string} [options.tilePosition] - Product position in list.
 * @return {JSON} add to cart response
 */
async function addToCartHandler(element, { tileContext, tilePosition } = {}) {
  const $element = element ? $(element) : $(this);
  let pidsObj;
  let setPids;

  // TODO REVISAR FUNCIONAMIENTO DE ERROR SHOW
  if ($element.hasClass('the-product-details__add-to-cart-btn--disabled')) {
    $('.the-product-details__add-to-cart-error').addClass(
      'the-product-details__add-to-cart-error--show'
    );
    return false;
  }

  $('body').trigger('product:beforeAddToCart', $element);

  // event triggered by cart-global
  if ($('.set-items').length && $element.hasClass('add-to-cart-global')) {
    setPids = [];

    $('.product-detail').each(function buildPids() {
      if (!$(this).hasClass('product-set-detail')) {
        setPids.push({
          pid: $(this).find('.product-id').text(),
          qty: $(this).find('.quantity-select').val(),
          options: getOptions($(this)),
        });
      }
    });
    pidsObj = JSON.stringify(setPids);
  }

  const pid = getPidValue($element);

  let $productContainer = $element.closest('.product-detail');
  if (!$productContainer.length) {
    $productContainer = $element
      .closest('.quick-view-dialog')
      .find('.product-detail');
  }

  const addToCartUrl = getAddToCartUrl();

  const wishlist = $element
    .closest('.product-card')
    .hasClass('product-card--wishlist');
  const form = {
    pid,
    pidsObj,
    childProducts: getChildProducts(),
    quantity: getQuantitySelected($element),
    removeSaveForLater: !$(element).data('is-limited'),
    wishlist: !!wishlist,
  };

  /** Customizable Products */
  const customizationForm = document.getElementById('customizationForm');
  if (customizationForm) {
    const customizedTextInput = customizationForm.querySelector(
      '.the-product-details-customization-modal__input input'
    );
    const customizedColorButton = customizationForm.querySelector(
      '.the-product-details-customization-modal__swatch--selected'
    );
    const productCustomization = {
      text: customizedTextInput ? customizedTextInput.value : '',
      color: customizedColorButton
        ? customizedColorButton.dataset.colorCode
        : '',
    };

    form.productCustomization = JSON.stringify(productCustomization);
  }

  if (!$('.bundle-item').length) {
    form.options = getOptions($productContainer);
  }

  $element.trigger('updateAddToCartFormData', form);
  let asyncResponse;
  if (addToCartUrl) {
    await $.ajax({
      url: addToCartUrl,
      method: 'POST',
      data: form,
      success(data) {
        if (data.error) {
          // TODO REVISAR ESTADO ERROR
          $('body').trigger('product:afterAddToCartError', {});
        } else {
          handlePostCartAdd(data);
          $('body').trigger('product:afterAddToCart', {
            ...data,
            tileContext,
            tilePosition,
          });
          miniCartReportingUrl(data.reportingURL);
          asyncResponse = data;
        }
      },
      error() {
        // TODO REVISAR ESTADO ERROR
        $('body').trigger('product:afterAddToCartError', {});
      },
    });
  }
  return asyncResponse;
}

/**
 * Sobreescribe el addToCart
 * @param {Object} dataToAdd Add to cart data
 * @param {Object} element data to send with the product:beforeAddToCart event
 * @return {JSON} add to cart response
 */
async function actionAddToCart(dataToAdd, element) {
  const addToCartUrl = getAddToCartUrl();

  $('body').trigger('product:beforeAddToCart', element);

  let asyncResponse;
  if (addToCartUrl) {
    await fetch(addToCartUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: dataToAdd,
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.error) {
          // TODO REVISAR ESTADO ERROR
          $('body').trigger('product:afterAddToCartError', {});
          // eslint-disable-next-line no-console
          console.warn('Message:', data.message);
        } else {
          handlePostCartAdd(data);
          $('body').trigger('product:afterAddToCart', data);
          miniCartReportingUrl(data.reportingURL);
        }
        asyncResponse = data;
      })
      .catch((error) => {
        // TODO REVISAR ESTADO ERROR
        $('body').trigger('product:afterAddToCartError', {});
        // eslint-disable-next-line no-console
        console.error('Error:', error);
      });
  }
  return asyncResponse;
}

/**
 * Returns product container for given dom element
 * @param {jQuery.Object|Element} element
 * @param {jQuery.Object} element
 */
function getProductContainer(element) {
  let $productContainer = $(element).closest('.product-detail-vue');

  const isCustomizationModal = $(element).closest(
    '.the-product-details-customization-modal'
  );
  if (isCustomizationModal.length && !$productContainer.length) {
    const pid = $(element).data('pid');
    $productContainer = $(`.product-detail-vue[data-pid="${pid}"]`);
  }

  if (!$productContainer.length) {
    $productContainer = $(element).closest('.set-item');
  }
  if (!$productContainer.length) {
    $productContainer = $(element).closest('.product-detail');
  }
  return $productContainer;
}

/**
 * Return true if product container is a Vue component
 * @param {jQuery.Object} $productContainer product container element
 * @returns {boolean} true if it's vue component
 */
function isVueProductContainer($productContainer) {
  return $productContainer.hasClass('product-detail-vue');
}

/**
 * Init mobile add to bag listeners, similar to addToCart for desktop
 * adds listeners for mobile's add to cart click interaction and size selector
 */
function addToCartMobile() {
  // CASE NO SIZE SELECTOR
  // takes attrUrl direct from data param
  $(document).on(
    'click',
    '.interaction--add-to-bag-mobile[data-size-type="one-size"]',
    (evt) => {
      evt.preventDefault();
      evt.stopPropagation();
      const element = evt.currentTarget;
      const container = getProductContainer(element);
      const { attrUrl } = element.dataset;

      $('body').trigger('productDetails:mobile:selectSizeAndAddToCart', {
        attrUrl,
        container,
        element,
      });
    }
  );

  // CASE SIZE SELECTOR
  // needs to capture attrUrl parameter from child label radio selector
  $(document).on(
    'click',
    '.interaction--add-to-bag-mobile[data-size-type="multiple-size"] .the-product-details-size-selector__container:not(.the-product-details-size-selector__container--disabled) .size-box',
    (evt) => {
      evt.preventDefault();
      evt.stopPropagation();
      const element = evt.currentTarget;
      const container = getProductContainer(element);
      const { attrUrl } = element.parentNode.querySelector(
        '.the-product-details-size-selector__size-input'
      ).dataset;

      $('body').trigger('productDetails:mobile:selectSizeAndAddToCart', {
        attrUrl,
        container,
        element,
      });
    }
  );

  // ON SELECT SIZE
  // waits for attribute selection, then excecute regular AddToCartHandler
  $('body').on(
    'productDetails:mobile:selectSizeAndAddToCart',
    async (evt, data) => {
      // Set interactionAddToCart property to avoid setting mobile button state to ENABLED before the product is added to the cart
      $('.interaction--add-to-bag-mobile').data('interactionAddToCart', true);
      await attributeSelect(data.attrUrl, data.container);
      addToCartHandler(data.element);
    }
  );
}

/**
 * Add the listeners to enable the quickshop workflow
 */
function quickShop() {
  // Step 0 listener
  $(document).on('click', '.base-product-tile-quick-shop__add', (evt) => {
    const quickShopButton = evt.currentTarget;
    const container = getProductContainer(quickShopButton);
    const containerElement = container?.[0];
    if (!containerElement?.dataset) {
      return;
    }
    const sliderContainer = containerElement.closest('.base-product-slider');
    const {
      analyticsEventBus,
      ANALYTICS_EVENTS,
    } = require('../analytics/events');
    analyticsEventBus.publish(ANALYTICS_EVENTS.productTileQuickshopClicked, {
      location: sliderContainer?.dataset.location,
      productId: containerElement.dataset.pid,
      recommender: {
        isEinstein:
          containerElement.dataset.isRecommendationFromEinstein === 'true',
      },
    });

    const hasUniqueSize =
      quickShopButton.dataset.attrUrl && quickShopButton.dataset.attrUrl !== '';
    if (hasUniqueSize && quickShopButton.dataset.step < 2) {
      const { context, key } = containerElement.dataset;
      const tileContext = context;
      const tilePosition = key?.split('-')?.pop() || undefined;
      $('body').trigger('quickShop:addSizeToCart', {
        attrUrl: quickShopButton.dataset.attrUrl,
        container,
        element: quickShopButton,
        tileContext,
        tilePosition,
      });
    }
  });

  // Step 1 listener
  $(document).on(
    'click',
    '.base-product-tile-quick-shop .base-product-tile-size-selector__size-input',
    (evt) => {
      const element = evt.currentTarget;
      const container = getProductContainer(element);
      const containerElement = container?.[0];
      if (!containerElement?.dataset) {
        return;
      }
      const { context, key } = containerElement.dataset;
      const tileContext = context;
      const tilePosition = key?.split('-')?.pop() || undefined;
      $('body').trigger('quickShop:addSizeToCart', {
        attrUrl: element.dataset.attrUrl,
        container,
        element,
        tileContext,
        tilePosition,
      });
    }
  );

  // Phase 1 & 2 handler
  $('body').on(
    'quickShop:addSizeToCart',
    /**
     * Handler for the custom `quickShop:addSizeToCart` event.
     *
     * This event is fired when a product variation is added to the cart
     * from the quick shop component.
     *
     * @event quickShop:addSizeToCart
     * @param {JQuery.Event} evt - The jQuery event object.
     * @param {Object} data - Extra data provided with the trigger.
     * @param {string} data.attrUrl - URL of the product attribute/variation to request.
     * @param {jQuery} data.container - Container where the response is rendered.
     * @param {HTMLElement} data.element - The DOM element that triggered the event.
     * @param {string} [data.tileContext] - Context of the product tile (e.g. section of the page).
     * @param {number} [data.tilePosition] - Position of the product tile in the listing/grid.
     *
     * @returns {Promise<void>} A promise that resolves once the product
     *   variation has been fetched and processed.
     */
    async (evt, data) => {
      const productVariation = await attributeSelect(
        data.attrUrl,
        data.container
      );
      const cartAddProduct = await addToCartHandler(data.element, {
        tileContext: data.tileContext,
        tilePosition: data.tilePosition,
      });
      if (productVariation && cartAddProduct) {
        $('body').trigger('quickShop:afterAddSizeToCart', {
          productVariation,
          cartAddProduct,
        });
      }
    }
  );
}

module.exports = {
  bylCustomInit() {
    addToCartMobile();
    quickShop();
  },
  attributeSelect,
  methods: baseModuleExports.methods,
  focusChooseBonusProductModal: baseModuleExports.focusChooseBonusProductModal, // listeners
  onClosingChooseBonusProductModal:
    baseModuleExports.onClosingChooseBonusProductModal, // listeners
  trapChooseBonusProductModalFocus:
    baseModuleExports.trapChooseBonusProductModalFocus, // listeners
  colorAttribute() {
    // base.colorAttribute(); Don't listen to [data-attr="color"] button

    $('body').on('product:selectColor', (e, data) => {
      const colorUrl = data.color.url;
      const { container } = data;
      attributeSelect(colorUrl, container);
    });
  }, // listeners
  selectAttribute: baseModuleExports.selectAttribute, // listeners
  availability: baseModuleExports.availability, // listeners
  addToCart() {
    // DESKTOP INTERACTIONS
    $(document).on(
      'click',
      'button.add-to-cart, button.add-to-cart-global',
      (event) => {
        addToCartHandler(event.currentTarget);
      }
    );
  },
  selectBonusProduct: baseModuleExports.selectBonusProduct, // listeners
  removeBonusProduct: baseModuleExports.removeBonusProduct, // listeners
  enableBonusProductSelection: baseModuleExports.enableBonusProductSelection, // listeners
  showMoreBonusProducts: baseModuleExports.showMoreBonusProducts, // listeners
  addBonusProductsToCart() {
    $(document).on('click', '.add-bonus-products', () => {
      const $readyToOrderBonusProducts = $(
        '.choose-bonus-product-dialog .selected-pid'
      );
      let queryString = '?pids=';
      const url = $('.choose-bonus-product-dialog').data('addtocarturl');
      const pidsObject = {
        bonusProducts: [],
      };

      $.each($readyToOrderBonusProducts, function buildBonusProducts() {
        const qtyOption = parseInt($(this).data('qty'), 10);

        let option = null;
        if (qtyOption > 0) {
          if (
            $(this).data('optionid') &&
            $(this).data('option-selected-value')
          ) {
            option = {};
            option.optionId = $(this).data('optionid');
            option.productId = $(this).data('pid');
            option.selectedValueId = $(this).data('option-selected-value');
          }
          pidsObject.bonusProducts.push({
            pid: $(this).data('pid'),
            qty: qtyOption,
            options: [option],
          });
          pidsObject.totalQty = parseInt($('.pre-cart-products').html(), 10);
        }
      });
      queryString += JSON.stringify(pidsObject);
      queryString = `${queryString}&uuid=${$(
        '.choose-bonus-product-dialog'
      ).data('uuid')}`;
      queryString = `${queryString}&pliuuid=${$(
        '.choose-bonus-product-dialog'
      ).data('pliuuid')}`;
      $.spinner().start();
      $.ajax({
        url: url + queryString,
        method: 'POST',
        success(data) {
          $.spinner().stop();
          if (data.error) {
            $('#chooseBonusProductModal').modal('hide');
          } else {
            $('.configure-bonus-product-attributes').html(data);
            $('.bonus-products-step2').removeClass('hidden-xl-down');
            $('#chooseBonusProductModal').modal('hide');
            $('.minicart').html(data.totalQty);
            setTimeout(() => {
              if ($('.cart-content').length) {
                window.location.reload();
              }
            }, 1500);
          }
        },
        error() {
          $.spinner().stop();
        },
      });
    });
  },
  actionAddToCart,
  getPidValue,
  getQuantitySelected: baseModuleExports.getQuantitySelected,
  handlePostCartAdd,
  miniCartReportingUrl: baseModuleExports.miniCartReportingUrl,
};
